import { useState, useEffect } from "react";
import { Form } from "react-admin";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import { makeStyles } from "@mui/styles";
import LoadingOverlay from "../../../core/LoadingOverlay";
import ConfirmDialog from "../../../core/ConfirmDialog";
import DismissibleMessage from "../DismissibleMessage";
import { calibrationService } from "../../../../services/CalibrationService";
import { ClientsDropdown, ProjectsDropdown, DatetimeDropdown } from "../DropdownV2";
import { PHASES, toggleConsecutivePhase, computePhase3DateRange, buildPhaseExtraSummary } from "./phaseSelection";
import { PhaseHeader, StartEndDateFields, ResumeCheckbox } from "./phaseControls";

const DEFAULT_GROUP_KEY = "default";

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    dropdownRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    phaseCard: {
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        margin: "8px 0",
        width: "100%",
        maxWidth: "700px",
        backgroundColor: "#fafafa",
    },
    buttonRow: {
        display: "flex",
        justifyContent: "center",
        margin: "20px 0",
    },
}));

export default function SingleProjectKickoff() {
    const classes = useStyles();
    const [clientName, setClientName] = useState(null);
    const [projectName, setProjectName] = useState(null);
    const [phase1Version, setPhase1Version] = useState(null);
    const [phase2Version, setPhase2Version] = useState(null);
    const [phase3Version, setPhase3Version] = useState(null);
    const [phase4Version, setPhase4Version] = useState(null);
    const [selectedPhases, setSelectedPhases] = useState([1, 2, 3, 4]);
    // Phase 1 resume disables the backend's default overwrite (extra.overwrite = false)
    const [phase1Resume, setPhase1Resume] = useState(false);
    // Phase 3 options
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [phase3Resume, setPhase3Resume] = useState(false);
    const [palmVersion, setPalmVersion] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const phaseVersions = {
        1: phase1Version,
        2: phase2Version,
        3: phase3Version,
        4: phase4Version,
    };
    const phaseSetters = {
        1: setPhase1Version,
        2: setPhase2Version,
        3: setPhase3Version,
        4: setPhase4Version,
    };

    useEffect(() => {
        setProjectName(null);
        clearVersions();
    }, [clientName]);

    useEffect(() => {
        clearVersions();
    }, [projectName]);

    const clearVersions = () => {
        setPhase1Version(null);
        setPhase2Version(null);
        setPhase3Version(null);
        setPhase4Version(null);
        setPalmVersion(null);
        setErrorMessage(null);
        setStatusMessage(null);
    };

    const togglePhase = (phaseNumber) => {
        setSelectedPhases((prev) => toggleConsecutivePhase(prev, phaseNumber));
    };

    const buildInputs = () => {
        const inputs = [];
        for (const phaseNum of selectedPhases) {
            const phase = PHASES.find((p) => p.number === phaseNum);
            const version = phaseVersions[phaseNum];
            if (version) {
                inputs.push({
                    dataset_type: phase.datasetType,
                    group_key: DEFAULT_GROUP_KEY,
                    version_dttm: version,
                });
            }
        }
        // SpanPalmCoefficients is an optional supplemental input for Phase 3
        if (selectedPhases.includes(3) && palmVersion) {
            inputs.push({
                dataset_type: "span-palm-coefficients",
                group_key: DEFAULT_GROUP_KEY,
                version_dttm: palmVersion,
            });
        }
        return inputs;
    };

    // Date validation for Phase 3
    const dateState = computePhase3DateRange(startDate, endDate);
    const { effectiveStart, effectiveEnd, dateRangeError } = dateState;

    const firstSelectedPhase = selectedPhases.length > 0 ? Math.min(...selectedPhases) : null;
    const canSubmit =
        clientName &&
        projectName &&
        selectedPhases.length > 0 &&
        phaseVersions[firstSelectedPhase] &&
        !submitting &&
        !dateRangeError;

    const handleSubmit = () => {
        setConfirmModalOpen(false);
        setSubmitting(true);
        setErrorMessage(null);
        setStatusMessage(null);

        const extra = { phases: selectedPhases };
        // Backend defaults overwrite=True; Phase 1 "resume" opts out of that.
        if (selectedPhases.includes(1) && phase1Resume) {
            extra.overwrite = false;
        }
        if (selectedPhases.includes(3)) {
            // Skip dates that are today or in the future — backend will use its default
            if (effectiveStart) extra.start_date = effectiveStart.toISOString();
            if (effectiveEnd) extra.end_date = effectiveEnd.toISOString();
            if (phase3Resume) extra.resume = true;
        }

        calibrationService
            .triggerPitPipeline(clientName, projectName, extra, buildInputs())
            .catch((err) => {
                const isTimeout = err === "Network Error" || err?.code === "ERR_NETWORK";
                if (!isTimeout) {
                    setErrorMessage(`Failed to submit: ${err}`);
                }
            });

        const phaseLabel =
            selectedPhases.length === PHASES.length ? "All phases" : `Phase(s) ${selectedPhases.join(", ")}`;
        setStatusMessage(`${phaseLabel} submitted. Check Progress Viewer for status.`);
        setSubmitting(false);
    };

    const progressViewerUrl = `/calibration/progressViewer?client=${encodeURIComponent(
        clientName || ""
    )}&project=${encodeURIComponent(projectName || "")}&processTypes=TriggerPitPipeline`;

    const confirmMessage = [
        `Run phase(s) ${selectedPhases.join(", ")} for client "${clientName}" and project "${projectName}"?`,
        ...buildPhaseExtraSummary({
            selectedPhases,
            phase1Resume,
            phase3Resume,
            startDate,
            endDate,
            effectiveStart,
            effectiveEnd,
        }),
    ].join("\n\n");

    return (
        <div style={{ paddingBottom: "200px" }}>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm Historical DLR Pipeline Trigger"
                handleClose={() => setConfirmModalOpen(false)}
                handleConfirm={handleSubmit}
                message={confirmMessage}
            />
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                {statusMessage && (
                    <div style={{ textAlign: "center", marginBottom: "10px" }}>
                        <Link to={progressViewerUrl} target="_blank" rel="noopener noreferrer">
                            View in Progress Viewer
                        </Link>
                    </div>
                )}
                <LoadingOverlay isLoading={submitting} message="Triggering Historical DLR Pipeline..." />
                <div className={classes.dropdownRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                    <ProjectsDropdown
                        clientName={clientName}
                        value={projectName}
                        setValue={setProjectName}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                </div>

                {PHASES.map((phase) => (
                    <div key={phase.number} className={classes.phaseCard}>
                        <PhaseHeader
                            phase={phase}
                            checked={selectedPhases.includes(phase.number)}
                            onToggle={() => togglePhase(phase.number)}
                        />
                        {selectedPhases.includes(phase.number) && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                    <DatetimeDropdown
                                        clientName={clientName}
                                        projectName={projectName}
                                        datasetType={phase.datasetType}
                                        group={DEFAULT_GROUP_KEY}
                                        value={phaseVersions[phase.number]}
                                        setValue={phaseSetters[phase.number]}
                                        name={phase.datasetLabel}
                                        styleClass={{ flex: 1 }}
                                    />
                                    {phase.number === 1 && (
                                        <ResumeCheckbox
                                            checked={phase1Resume}
                                            onChange={setPhase1Resume}
                                            caption="Reuse existing outputs instead of overwriting"
                                        />
                                    )}
                                </div>
                                {phase.number === 3 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "16px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <StartEndDateFields
                                            startDate={startDate}
                                            setStartDate={setStartDate}
                                            endDate={endDate}
                                            setEndDate={setEndDate}
                                            dateState={dateState}
                                        />
                                        <DatetimeDropdown
                                            clientName={clientName}
                                            projectName={projectName}
                                            datasetType="span-palm-coefficients"
                                            group={DEFAULT_GROUP_KEY}
                                            value={palmVersion}
                                            setValue={setPalmVersion}
                                            name="PALM Coefficients (optional)"
                                            styleClass={{ minWidth: "220px" }}
                                        />
                                        <ResumeCheckbox checked={phase3Resume} onChange={setPhase3Resume} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={!canSubmit}
                        onClick={() => setConfirmModalOpen(true)}
                    >
                        {selectedPhases.length === PHASES.length
                            ? "Run All Phases"
                            : `Run Phase${selectedPhases.length > 1 ? "s" : ""} ${selectedPhases.join(", ")}`}
                    </Button>
                </div>
            </Form>
        </div>
    );
}
